import {
    DEFAULT_CRITERIA_PARAMETER_NAME,
    parseSearchCriteria
} from '@corratech/pylot-filters-and-sort/src/hooks/use-plp-manager'
import type { Option } from '@pylot-data/hooks/product/use-product-ui'
import type { ConfigurableProductOptions } from '@pylot-data/pylotschema'
import { useRouter } from 'next/router'
import type { Dispatch, SetStateAction } from 'react'
import { useEffect, useMemo, useState } from 'react'
import type { SelectedOptions } from '../../types/SelectedOptions'
import { convertPreselectedOptions } from './utils/convertPreselectedOptions'
import { getDefaultSelectedOptions } from './utils/getDefaultSelectedOptions'
import { getProductOptions } from './utils/getProductOptions'

export type UseOptionSelectorReturn = {
    selectedOptions: SelectedOptions
    setSelectedOptions: Dispatch<SetStateAction<SelectedOptions>>
    options: Option[]
}

/**
 * Hook that is responsible for product option API.
 * Should be used in combination with `<VariantSelector />`.
 * @param configurableOptions product configurable options
 * @param preselectedOptions preselected options, e.g. on CartItem.
 * Example: { 93: "Black White", 187: "S" }
 * @param showFirstProductAsDefault
 * @returns product options API
 */
export const useOptionSelector = (
    configurableOptions: ConfigurableProductOptions[] | undefined,
    preselectedOptions?: Record<string, string>,
    showFirstProductAsDefault?: boolean
): UseOptionSelectorReturn => {
    const { query, asPath } = useRouter()
    const filterParams = useMemo(
        () =>
            parseSearchCriteria(
                query[DEFAULT_CRITERIA_PARAMETER_NAME] as string
            ),
        [query]
    )

    const [selectedOptions, setSelectedOptions] = useState<SelectedOptions>(
        getDefaultSelectedOptions(configurableOptions, filterParams)
    )

    const options = useMemo(() => getProductOptions(configurableOptions), [
        configurableOptions
    ])

    const getOptionValues = (attributeCode: string) => {
        return (
            (configurableOptions &&
                configurableOptions.find(
                    (option) => option.attribute_code == attributeCode
                )?.values) ||
            []
        )
    }

    const getAttributeName = (id: string, code: string) => {
        if (configurableOptions) {
            const optionValues = getOptionValues(code)

            if (optionValues) {
                return optionValues.find(
                    (value) => value?.value_index?.toString() === id
                )?.store_label
            }
        }
    }

    // Update selected options to have initial values
    // when configurable options are not set and are loaded
    useEffect(() => {
        if (selectedOptions && !Object.keys(selectedOptions).length) {
            const defaultOptions = getDefaultSelectedOptions(
                configurableOptions,
                filterParams
            )
            setSelectedOptions(() => defaultOptions)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [configurableOptions?.length, setSelectedOptions, query])

    // Set selected options from `preselectedOptions` prop.
    // Reset options on url change (PDP -> PDP transition)
    useEffect(() => {
        const convertedPreselectedOptions = convertPreselectedOptions(
            configurableOptions,
            preselectedOptions
        )

        const defaultOptions = getDefaultSelectedOptions(
            configurableOptions,
            filterParams,
            showFirstProductAsDefault
        )
        const optionsFromPreselected = {
            ...defaultOptions,
            ...convertedPreselectedOptions
        }

        setSelectedOptions(optionsFromPreselected)

        // Is it important to stringify, otherwise after the first selection selected
        // options jump back to the preselected ones due to the extra useEffect cycle.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        asPath,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        JSON.stringify(configurableOptions),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        JSON.stringify(preselectedOptions)
        //setSelectedOptions
    ])

    useEffect(() => {
        const urlParams: { [key: string]: string } = {}
        const locationParam = location.search.split('=')
        if (locationParam.length > 1) {
            const queryFilter = locationParam.length > 1 ? locationParam[1] : ''
            const filters = queryFilter.split('-')

            filters.forEach((filter) => {
                const [attributeCode, attributeId] = filter.split(':')
                const attributeName = getAttributeName(
                    attributeId,
                    attributeCode
                )
                if (attributeName) urlParams[attributeCode] = attributeName
            })

            if (Object.keys(urlParams).length !== 0)
                setSelectedOptions({ ...selectedOptions, ...urlParams })
        }
    }, [])

    return {
        options,
        selectedOptions,
        setSelectedOptions
    }
}
